import { h } from '@stencil/core';
import { addMutationObserver, updateContainerPosition } from "../../utils/dom";
import { calendarI18n } from './calendar-i18n';
import { i18n, findLocale } from '../../utils/I18n';
import { formatDate, months, isAustrianHoliday, isLastDayOfMonth } from '../../utils/date';
export class Calendar {
  constructor() {
    this.today = new Date();
    this.visible = false;
    this.daysList = [];
    this.date = new Date();
    this.focusedDate = undefined;
    this.currentMonth = undefined;
    this.nextMonthDisabled = undefined;
    this.prevMonthDisabled = undefined;
    this.mode = undefined;
    this.inputSelector = undefined;
    this.disabledBefore = undefined;
    this.disabledAfter = undefined;
    this.disableSaturdays = false;
    this.disableSundays = false;
    this.disableWeekends = false;
    this.disableHolidays = false;
  }
  componentWillLoad() {
    this.lang = findLocale(this.el);
    i18n.setLang(this.lang);
    i18n.add(calendarI18n);
    if (this.inputSelector) {
      this.dateInput = this.el.parentNode.querySelector(this.inputSelector);
    }
    this.date = new Date();
    this.today = new Date();
    if (this.disabledBefore)
      this.disabledBefore.setHours(0, 0, 0, 0);
    if (this.disabledAfter)
      this.disabledAfter.setHours(0, 0, 0, 0);
    this.updateCurrentMonth();
  }
  componentDidLoad() {
    if (this.mode === '2022') {
      this.mutationObserver = addMutationObserver(() => updateContainerPosition(this.dateInput, this.el));
    }
  }
  componentWillUpdate() {
    if (this.disabledBefore)
      this.disabledBefore.setHours(0, 0, 0, 0);
    if (this.disabledAfter)
      this.disabledAfter.setHours(0, 0, 0, 0);
  }
  disconnectedCallback() {
    var _a;
    (_a = this.mutationObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
  }
  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.hide();
    }
    else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.changeFocusedDate(0, -1);
    }
    else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.changeFocusedDate(0, 1);
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.changeFocusedDate(0, -7);
    }
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.changeFocusedDate(0, 7);
    }
    else if (e.key === 'Enter') {
      if (!e.composedPath().includes(this.el.shadowRoot.querySelector('.calendar-header'))) {
        // Catch enter only if it didn't originate from the header buttons
        e.preventDefault();
        this.selectFocusedDate();
      }
    }
  }
  async setDateInput(dateInput) {
    this.dateInput = dateInput;
  }
  async focusCalendar() {
    var _a, _b;
    await this.show();
    (_b = (_a = this.el) === null || _a === void 0 ? void 0 : _a.shadowRoot.querySelector('.calendar')) === null || _b === void 0 ? void 0 : _b.focus();
  }
  async toggle() {
    return this.visible ? this.hide() : this.show();
  }
  async show() {
    var _a;
    if (!this.visible && this.date) {
      // If calendar was hidden jump to the previously selected date
      this.focusedDate = null;
    }
    this.el.style.display = 'inline-block';
    this.visible = true;
    (_a = this.dateInput) === null || _a === void 0 ? void 0 : _a.classList.add('calendar-open');
    switch (this.mode) {
      case 'default':
        const rect = this.dateInput.getBoundingClientRect();
        this.el.style.top = rect.height + 6 + 'px';
        this.el.style.left = '0';
        break;
      case '2022':
        updateContainerPosition(this.dateInput, this.el);
        break;
    }
    this.updateCurrentMonth();
    this.el.scrollIntoView({ block: 'nearest' });
  }
  async hide() {
    var _a;
    this.el.style.display = 'none';
    this.visible = false;
    (_a = this.dateInput) === null || _a === void 0 ? void 0 : _a.classList.remove('calendar-open');
  }
  async setDate(date) {
    this.date = date;
    this.focusedDate = null;
    this.updateCurrentMonth();
  }
  changeFocusedDate(addMonths = 0, addDays = 0) {
    var _a;
    const focusedDate = (_a = this.focusedDate) !== null && _a !== void 0 ? _a : this.date;
    const newFocusedDateDay = isLastDayOfMonth(focusedDate) ? 1 : focusedDate.getDate();
    const newFocusedDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + addMonths, newFocusedDateDay + addDays);
    this.focusedDate = snapToRange(newFocusedDate, this.disabledBefore, this.disabledAfter);
    this.updateCurrentMonth();
  }
  updateDisabled() {
    // Determine whether prev/next month are enabled
    this.prevMonthDisabled = this.currentMonth <= this.disabledBefore;
    this.nextMonthDisabled = isSameMonth(this.currentMonth, this.disabledAfter);
    this.moveFocusToActiveElement();
  }
  moveFocusToActiveElement() {
    const activeElement = this.el.shadowRoot.activeElement;
    if (!activeElement) {
      return;
    }
    if (this.prevMonthDisabled && activeElement.classList.contains('previous-btn')) {
      // Button for previous month is focused, but will be disabled
      this.el.focus();
    }
    else if (this.nextMonthDisabled && activeElement.classList.contains('next-btn')) {
      // Button for next month is focused, but will be disabled
      this.el.focus();
    }
  }
  updateCurrentMonth() {
    var _a;
    const focusedDate = (_a = this.focusedDate) !== null && _a !== void 0 ? _a : this.date;
    if (isSameMonth(focusedDate, this.currentMonth)) {
      // Focused date matches the shown month, only update the month switches
      this.updateDisabled();
      return;
    }
    // Store the requested month
    this.currentMonth = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1);
    // Determine whether prev/next month are enabled
    this.updateDisabled();
    // Collect dates until the last date of the requested month is reached
    this.daysList = [];
    let currentDate = this.currentMonth;
    while (currentDate.getMonth() === this.currentMonth.getMonth()) {
      this.daysList.push(currentDate);
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
    }
  }
  selectFocusedDate() {
    if (!this.focusedDate) {
      this.hide();
    }
    else if (!this.isDateDisabled(this.focusedDate)) {
      this.onDayClick(this.focusedDate);
    }
  }
  onDayClick(date) {
    this.date = date;
    this.dateInput.value = formatDate(date);
    this.dateChanged.emit(date);
    this.hide();
  }
  isDateDisabled(date) {
    date.setHours(0, 0, 0, 0);
    if (date < this.disabledBefore || date > this.disabledAfter) {
      return true;
    }
    if (this.disableSaturdays && (date.getDay() == 6)) {
      return true;
    }
    if (this.disableSundays && (date.getDay() == 0)) {
      return true;
    }
    if (this.disableHolidays && isAustrianHoliday(date)) {
      return true;
    }
    if (this.disableWeekends && (date.getDay() == 0 || date.getDay() == 6)) {
      return true;
    }
  }
  render() {
    return (h("div", { class: "calendar", tabindex: "-1", onClick: (e) => e.stopPropagation() }, h("div", { class: "calendar-header" }, h("h3", null, i18n.get(months[this.currentMonth.getMonth()]), ' ', this.currentMonth.getFullYear()), h("button", { type: "button", class: "calendar__close-btn", title: i18n.get('close-calendar', 'Kalender schließen'), onClick: () => this.hide() }, h("oebbx-icon", { name: "close", width: "24", height: "24", "aria-hidden": "true" }), h("span", { class: "screen-reader-text" }, i18n.get('close-calendar', 'Kalender schließen'))), h("button", { type: "button", class: "previous-btn", title: i18n.get('previous-month', 'Vorheriger Monat'), disabled: this.prevMonthDisabled, onClick: () => this.changeFocusedDate(-1) }, h("oebbx-icon", { name: "triangle", width: "20", height: "20", svgStyle: { transform: 'rotate(180deg)' }, "aria-hidden": "true" }), h("span", { class: "screen-reader-text" }, i18n.get('previous-month', 'Vorheriger Monat'))), h("button", { type: "button", class: "next-btn", title: i18n.get('next-month', 'Nächster Monat'), disabled: this.nextMonthDisabled, onClick: () => this.changeFocusedDate(1) }, h("oebbx-icon", { name: "triangle", width: "20", height: "20", "aria-hidden": "true" }), h("span", { class: "screen-reader-text" }, i18n.get('next-month', 'Nächster Monat')))), h("ul", { class: "header" }, h("li", null, i18n.get('monday', 'MO')), h("li", null, i18n.get('tuesday', 'DI')), h("li", null, i18n.get('wednesday', 'MI')), h("li", null, i18n.get('thursday', 'DO')), h("li", null, i18n.get('friday', 'FR')), h("li", null, i18n.get('saturday', 'SA')), h("li", { class: "sunday" }, i18n.get('sunday', 'SO'))), h("ul", { class: "days", tabindex: -1 }, this.daysList.map(date => (h("li", { class: {
        'disabled': this.isDateDisabled(date),
        [`dow-${date.getDay()}`]: true,
      } }, h("button", { type: "button", tabindex: isSameDate(date, this.date) || isSameDate(date, this.focusedDate) ? "0" : "-1", class: {
        'today': isSameDate(date, this.today),
        'active': isSameDate(date, this.date),
        'focused': isSameDate(date, this.focusedDate),
      }, disabled: this.isDateDisabled(date), onClick: () => this.onDayClick(date) }, date.getDate()))))), h("div", { class: "calendar-footer" }, h("button", { type: "button", class: "footer-btn", onClick: () => this.onDayClick(new Date()) }, i18n.get('today', 'heute')))));
  }
  static get is() { return "oebbx-calendar"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "2021": ["calendar-2021.scss"],
      "2022": ["calendar-2022.scss"],
      "default": ["calendar.css"]
    };
  }
  static get styleUrls() {
    return {
      "2021": ["calendar-2021.css"],
      "2022": ["calendar-2022.css"],
      "default": ["calendar.css"]
    };
  }
  static get properties() {
    return {
      "mode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "mode",
        "reflect": false
      },
      "inputSelector": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "input-selector",
        "reflect": false
      },
      "disabledBefore": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Date",
          "resolved": "Date",
          "references": {
            "Date": {
              "location": "global"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "disabledAfter": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Date",
          "resolved": "Date",
          "references": {
            "Date": {
              "location": "global"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "disableSaturdays": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "disable-saturdays",
        "reflect": false,
        "defaultValue": "false"
      },
      "disableSundays": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "disable-sundays",
        "reflect": false,
        "defaultValue": "false"
      },
      "disableWeekends": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "disable-weekends",
        "reflect": false,
        "defaultValue": "false"
      },
      "disableHolidays": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "disable-holidays",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "date": {},
      "focusedDate": {},
      "currentMonth": {},
      "nextMonthDisabled": {},
      "prevMonthDisabled": {}
    };
  }
  static get events() {
    return [{
        "method": "dateChanged",
        "name": "dateChanged",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }];
  }
  static get methods() {
    return {
      "setDateInput": {
        "complexType": {
          "signature": "(dateInput: HTMLInputElement) => Promise<void>",
          "parameters": [{
              "tags": [],
              "text": ""
            }],
          "references": {
            "Promise": {
              "location": "global"
            },
            "HTMLInputElement": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      },
      "focusCalendar": {
        "complexType": {
          "signature": "() => Promise<void>",
          "parameters": [],
          "references": {
            "Promise": {
              "location": "global"
            },
            "HTMLElement": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      },
      "toggle": {
        "complexType": {
          "signature": "() => Promise<void>",
          "parameters": [],
          "references": {
            "Promise": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      },
      "show": {
        "complexType": {
          "signature": "() => Promise<void>",
          "parameters": [],
          "references": {
            "Promise": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      },
      "hide": {
        "complexType": {
          "signature": "() => Promise<void>",
          "parameters": [],
          "references": {
            "Promise": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      },
      "setDate": {
        "complexType": {
          "signature": "(date: Date) => Promise<void>",
          "parameters": [{
              "tags": [],
              "text": ""
            }],
          "references": {
            "Promise": {
              "location": "global"
            },
            "Date": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      }
    };
  }
  static get elementRef() { return "el"; }
  static get listeners() {
    return [{
        "name": "keydown",
        "method": "handleKeydown",
        "target": undefined,
        "capture": false,
        "passive": false
      }];
  }
}
function snapToRange(date, minDate, maxDate) {
  if (!date) {
    return null;
  }
  if (minDate && maxDate && minDate > maxDate) {
    return null;
  }
  if (minDate && minDate > date) {
    return minDate;
  }
  if (maxDate && maxDate < date) {
    return maxDate;
  }
  return date;
}
function isSameMonth(a, b) {
  return a && b && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}
function isSameDate(a, b) {
  return isSameMonth(a, b) && a.getDate() === b.getDate();
}
//# sourceMappingURL=calendar.js.map
